import { createStore } from 'react-sweet-state';
import { cloneDeep } from 'lodash';

import { sortBrandTerms } from 'src/data/brandTerm';
import {
  queryBrandTerms,
  createBrandTerm,
  updateBrandTerm,
  deleteBrandTerm,
  applyBrandTerm,
} from 'src/graphql/brandTerms';
import { captureMessage } from 'src/utils/lib/sentry';

const INITIAL_STATE = {
  brandTerms: [],
  loadingBrandTerms: false,
  savingBrandTerm: false,
  deletingBrandTerm: false,

  applyingChanges: false,
};

/**
 * Private actions
 */
const PRIVATE_ACTIONS = {
  clearStore:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  mergeToolInCache:
    (savedTool) =>
    ({ getState, setState }) => {
      const { brandTerms: list } = getState();
      const toolExists = list.some((t) => t.id === savedTool.id);
      const newList = toolExists
        ? list.map((t) => (t.id === savedTool.id ? savedTool : t))
        : sortBrandTerms([...list, savedTool]);
      setState({ brandTerms: newList });
    },

  removeToolFromCache:
    (toolId) =>
    ({ getState, setState }) => {
      const { brandTerms } = getState();
      const filteredList = brandTerms.filter((t) => t.id !== toolId);
      setState({ brandTerms: filteredList });
    },
};

/**
 * Public actions
 */
const ACTIONS = {
  init:
    () =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.clearStore());
    },

  queryBrandTerms:
    (workspaceId) =>
    async ({ setState }) => {
      setState({ loadingBrandTerms: true });
      try {
        const result = await queryBrandTerms(workspaceId);

        const sortedItems = sortBrandTerms(result);
        setState({ brandTerms: sortedItems });
      } catch (error) {
        captureMessage('Error while getting brand terms', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'BrandTermsStore -> queryBrandTerms',
          },
        });
      } finally {
        setState({ loadingBrandTerms: false });
      }
    },

  getBrandTerm:
    (itemId) =>
    ({ getState }) => {
      const { brandTerms } = getState();
      return brandTerms.find((t) => t.id === itemId);
    },

  createBrandTerm:
    (item) =>
    async ({ setState, dispatch }, { currentWorkspaceId }) => {
      setState({ savingBrandTerm: true });
      try {
        const createdTool = await createBrandTerm({
          workspaceId: currentWorkspaceId,
          ...item,
        });
        dispatch(PRIVATE_ACTIONS.mergeToolInCache(createdTool));
        return createdTool;
      } finally {
        setState({ savingBrandTerm: false });
      }
    },

  updateBrandTerm:
    (item) =>
    async ({ setState, dispatch }, { currentWorkspaceId }) => {
      setState({ savingBrandTerm: true });
      try {
        const updatedTool = await updateBrandTerm({
          workspaceId: currentWorkspaceId,
          ...item,
        });
        dispatch(PRIVATE_ACTIONS.mergeToolInCache(updatedTool));
        return updatedTool;
      } finally {
        setState({ savingBrandTerm: false });
      }
    },

  updateBrandTermStatus:
    (itemId, enabled) =>
    async ({ setState, dispatch }, { currentWorkspaceId }) => {
      setState({ savingBrandTerm: true });
      try {
        const updatedTool = await updateBrandTerm({
          workspaceId: currentWorkspaceId,
          id: itemId,
          enabled,
        });
        dispatch(PRIVATE_ACTIONS.mergeToolInCache(updatedTool));
        return updatedTool;
      } finally {
        setState({ savingBrandTerm: false });
      }
    },

  deleteBrandTerm:
    (itemId) =>
    async ({ setState, dispatch }, { currentWorkspaceId }) => {
      setState({ deletingBrandTerm: true });
      try {
        await deleteBrandTerm({ workspaceId: currentWorkspaceId, id: itemId });
        dispatch(PRIVATE_ACTIONS.removeToolFromCache(itemId));
      } finally {
        setState({ deletingBrandTerm: false });
      }
    },

  setApplyingChanges:
    (applyingChanges) =>
    ({ setState }) => {
      setState({ applyingChanges });
    },

  applyDetectedBrandTerm:
    (originalText, detectedBrandTerm, options) =>
    async ({ setState }) => {
      if (!options?.avoidApplyingState) {
        setState({ applyingChanges: true });
      }

      try {
        const result = await applyBrandTerm({
          originalText,
          termInText: detectedBrandTerm.termInText,
          replaceWith: detectedBrandTerm.brandTerm?.replaceWith || '',
        });

        return {
          changes: result.changes.map((change) => ({
            ...change,
            originText: change.originText || detectedBrandTerm.termInText,
          })),
        };
      } finally {
        if (!options?.avoidApplyingState) {
          setState({ applyingChanges: false });
        }
      }
    },

  isSavingBrandTerm:
    () =>
    ({ getState }) => {
      return getState().savingBrandTerm;
    },
};

export const BrandTermsStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'brand-terms-store',
});
